import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import _ from 'lodash';

import { RECONNECT_FALLBACK_DELAY_MS, RETRY_PERIOD_BY_RETRY_COUNT } from 'constants/signalR';

import { hasResumableSession } from './signalR.utils';

const buildReconnectPolicy = () => ({
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) => {
    if (!hasResumableSession()) {
      return null;
    }

    return _.get(RETRY_PERIOD_BY_RETRY_COUNT, previousRetryCount, RECONNECT_FALLBACK_DELAY_MS);
  },
});

class SignalRConnection {
  #connection;
  #logger;
  #wakeHandler;
  // DEV-3108: handlers re-run after a WAKE reconnect (the #maybeReconnect cold
  // start path), which does NOT fire onreconnected — so controllers register
  // their group re-join here too, otherwise live updates stay dead after a
  // laptop-sleep reconnect (partially defeating DEV-3055/DEV-3061).
  #wakeReconnectedHandlers = [];

  constructor(logger) {
    this.#logger = logger;
  }

  // DEV-3108: register a callback to run after a successful wake reconnect.
  onWakeReconnected(handler) {
    if (_.isFunction(handler)) {
      this.#wakeReconnectedHandlers.push(handler);
    }
  }

  get connectionId() {
    return this.#connection?.connectionId;
  }

  get isConnected() {
    return this.#connection?.state === HubConnectionState.Connected;
  }

  init(connectionURL, getToken) {
    if (this.#connection) {
      this.#logger.error('Trying to create new connection with live example of connection!');

      return;
    }

    this.#connection = new HubConnectionBuilder()
      .withUrl(connectionURL, {
        withCredentials: false,
        accessTokenFactory: getToken,
      })
      .withAutomaticReconnect(buildReconnectPolicy())
      .configureLogging(LogLevel.Information)
      .build();

    this.#attachWakeListeners();
  }

  // getToken is a FACTORY, not a token: SignalR calls it on every (re)connect, so a long-lived hub can
  // outlive the token it first negotiated instead of reconnecting forever on an expired one (DEV-6376).
  initAzure(connectionURL, getToken) {
    if (this.#connection) {
      this.#logger.error('Trying to create new connection with live example of connection!');

      return;
    }

    this.#connection = new HubConnectionBuilder()
      .withUrl(connectionURL, {
        withCredentials: false,
        accessTokenFactory: getToken,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect(buildReconnectPolicy())
      .configureLogging(LogLevel.Information)
      .build();

    this.#attachWakeListeners();
  }

  invoke(methodName, ...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to invoke without active connection!', methodName, ...args);

      return;
    }

    // Skip when the hub is reconnecting or disconnected. Calling invoke would
    // throw "Cannot send data if the connection is not in the Connected state"
    // and pollute the console — drop the call instead.
    if (this.#connection.state !== HubConnectionState.Connected) {
      return;
    }

    return this.#connection.invoke(methodName, ...args).catch(err => {
      this.#logger.error(err);
    });
  }

  onreconnecting(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to reconnect without active connection!', ...args);

      return;
    }

    return this.#connection?.onreconnecting(...args);
  }

  onreconnected(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to reconnect without active connection!', ...args);

      return;
    }

    return this.#connection.onreconnected(...args);
  }

  on(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to call "on" method without active connection!', ...args);

      return;
    }

    return this.#connection.on(...args);
  }

  off(methodName, handler) {
    if (!this.#connection) {
      this.#logger.error(
        `Trying to to call "off" method with ${methodName} argument without active connection!`,
      );

      return;
    }

    return this.#connection.off(methodName, handler);
  }

  start(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to start connection without active connection!', ...args);

      return;
    }

    return this.#connection.start(...args);
  }

  stop(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to stop connection without active connection!', ...args);

      return;
    }

    return this.#connection.stop(...args);
  }

  onclose(...args) {
    if (!this.#connection) {
      this.#logger.error('Trying to close connection without active connection!', ...args);

      return;
    }

    return this.#connection?.onclose(...args);
  }

  resetConnection() {
    this.#detachWakeListeners();
    this.#wakeReconnectedHandlers = [];
    this.#connection = null;
  }

  // Wake recovery: a laptop sleep or network change can suspend the WebSocket
  // without SignalR's reconnect logic detecting it cleanly. When the tab
  // regains visibility or the network comes back online, re-arm the hub if it
  // has settled into Disconnected.
  #attachWakeListeners() {
    if (this.#wakeHandler) {
      return;
    }

    this.#wakeHandler = () => this.#maybeReconnect();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.#wakeHandler);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.#wakeHandler);
    }
  }

  #detachWakeListeners() {
    if (!this.#wakeHandler) {
      return;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#wakeHandler);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.#wakeHandler);
    }

    this.#wakeHandler = null;
  }

  #maybeReconnect() {
    if (!this.#connection) {
      return;
    }

    if (this.#connection.state !== HubConnectionState.Disconnected) {
      return;
    }

    if (!hasResumableSession()) {
      return;
    }

    this.#connection
      .start()
      .then(() => {
        // DEV-3108: a wake start() is a cold start and does NOT fire
        // onreconnected, so re-run the registered re-join handlers here.
        this.#wakeReconnectedHandlers.forEach(handler => {
          try {
            handler();
          } catch (err) {
            this.#logger.error?.('SignalR wake-reconnected handler failed', err);
          }
        });
      })
      .catch(err => {
        this.#logger.warn?.('SignalR wake reconnect failed', err);
      });
  }
}

export default SignalRConnection;
